# Client half of the component-read cookie. See
# framework/componentAccessCookie.coffee for the server half and for why a
# cookie is the only credential a component fetch can carry.
#
# The job is small and the timing is everything: a component bundle is fetched
# by `import()` or a `<script src>` tag, neither of which can send a header, so
# the cookie has to already be in the jar BEFORE the fetch goes out. Every
# component entry point therefore awaits ensureComponentCookie() first.
#
# Deliberately plain `fetch` rather than window.clientAPI. The admin's clientAPI
# PREFERS the socket transport, and a socket call has no HTTP response and
# therefore no Set-Cookie — minting over the socket would appear to succeed and
# set nothing. This must be HTTP, and it must be same-origin so the browser
# stores the cookie.

MINT_PATH = '/api/components/mintAccessCookie'
# Re-mint this many ms before the server's TTL runs out, so a component load
# never races the expiry it was just told about.
RENEW_MARGIN_MS = 60 * 1000

# `mintedFor` is the auth header the cached cookie was minted against. Keying on
# it means a principal change — logout, login as somebody else, a step-up that
# rotates the token — invalidates the cache on its own, with no hook to
# remember to call from the auth code. The alternative (an explicit reset from
# clearUser) is one refactor away from being forgotten, and the symptom would be
# a user silently loading components as the previous principal.
#
# The COOKIE itself still sits in the browser until it expires. That is bounded
# by the server's TTL (minutes) and it only ever grants component reads that
# principal's ACL already allowed, so the residue is a stale read window, not an
# escalation.
state = {promise: null, expiresAt: 0, mintedFor: null}

authHeader = ->
  # Whatever the app authenticated with. No session -> no header -> no cookie
  # is needed or possible.
  window.clientAPI?.headers?.Authorization or null

# Behind a TLS-terminating proxy (ngrok, nginx) the served config routinely
# carries an http:// basePath while the page itself runs https://. The framework
# already knows this and compensates elsewhere by comparing HOSTS rather than
# origins (client_api.coffee _isSelfInstance, card 6a7bd623a51b17ac84335a00).
#
# It has teeth here, because a cross-scheme URL is a DIFFERENT ORIGIN: the mint
# POST is then active mixed content, which Chrome blocks outright rather than
# auto-upgrading, and `credentials: 'same-origin'` would refuse to store the
# cookie even if it went out. Observed on the seat as a bare "TypeError: Failed
# to fetch" with nothing in the server log, because the request never left the
# browser.
#
# So: same host as this page means same instance — adopt the page's scheme. A
# genuinely different host is a re-pointed instance and is left exactly as
# configured, because there the cross-origin cookie story is a different design
# and must not be papered over here.
#
# This is a targeted fix for the component path. The general defect — instances
# serving an http:// basePath to an https:// page — is card
# 6a7d1dccb360d2199b2609d2 and is not solved here.
sameOriginify = (url) ->
  return url unless url
  try
    parsed = new URL(url, window.location.href)
    return url unless parsed.host == window.location.host
    parsed.protocol = window.location.protocol
    parsed.toString().replace(/\/$/, '')
  catch
    url

apiBase = ->
  sameOriginify(window.config?.basePath or '')

# Resolves when the cookie is in place, or when it is established that we cannot
# get one. NEVER rejects: an anonymous visitor and a failed mint must both fall
# through to the ordinary fetch, because unrestricted components load
# anonymously and are the overwhelming majority. Only a RESTRICTED component
# needs this to have succeeded, and that one will 404 loudly on its own.
ensureComponentCookie = (force = false) ->
  header = authHeader()
  # Anonymous. Do not POST on every public page load to be told "no cookie for
  # you" — the public website mounts components with no session at all.
  return Promise.resolve(false) unless header?

  now = Date.now()
  if not force and state.promise? and now < state.expiresAt and state.mintedFor == header
    return state.promise

  state.mintedFor = header
  state.promise = (->
    try
      res = await fetch "#{apiBase()}#{MINT_PATH}",
        method: 'POST'
        credentials: 'same-origin'   # the whole point: let the browser keep the cookie
        headers:
          'Authorization': header
          'Content-Type': 'application/json'
        body: '{}'
      unless res.ok
        console.warn "[componentCookie] mint returned #{res.status}; restricted components will not load for this session."
        state.expiresAt = 0
        return false
      body = await res.json()
      ttlMs = (body?.ttl or 0) * 1000
      state.expiresAt = if body?.minted then Date.now() + Math.max(0, ttlMs - RENEW_MARGIN_MS) else 0
      return !!body?.minted
    catch e
      console.warn "[componentCookie] mint failed:", e?.message or e
      state.expiresAt = 0
      return false
  )()
  state.promise

# Forget any cached mint. Called on logout / principal change so the next
# component load re-mints as whoever is logged in now instead of trusting a
# cookie issued to the previous principal.
resetComponentCookie = ->
  state.promise = null
  state.expiresAt = 0
  state.mintedFor = null

# Import a component bundle with the cookie guaranteed to be in place first, and
# ONE retry.
#
# The retry is not defensive padding. A module URL that fails once is remembered
# as failed in the browser's module map for the lifetime of the page, so the
# ordinary case — the cookie expired while the tab sat open — would otherwise be
# unrecoverable without a reload. The retry re-mints and appends a cache-buster
# so the second attempt is a genuinely new module URL rather than the remembered
# failure.
# The native import() is built at CALL time, not written in this file. Written
# here (it used to be, as backticked `import(/*webpackIgnore*/ url)`), it is
# syntax every bundle carrying this module contains, and an engine below
# Chrome 63 cannot parse the bundle at all — the whole app, not just component
# loading, fails at the browser baseline (framework/shared/browserBaseline.js,
# cards 6ac6811250bc89cabe370065 / ...0067). Built with Function, an engine
# without import() fails HERE, as a rejected load the caller already handles.
# The CSP allows it: securityHeaders.coffee scriptSrc carries 'unsafe-eval'.
nativeImport = null
rawImport = (url) ->
  try
    nativeImport ?= new Function('url', 'return import(url)')
  catch e
    return Promise.reject e
  nativeImport(url)

importComponentModule = (rawUrl) ->
  # Same scheme correction as the mint, and for the same reason: a component
  # module URL one scheme below the page is a cross-origin fetch, so the cookie
  # would not be attached to it even once minted.
  url = sameOriginify(rawUrl)
  await ensureComponentCookie()
  try
    return await rawImport(url)
  catch e
    minted = await ensureComponentCookie(true)
    throw e unless minted
    sep = if url.indexOf('?') == -1 then '?' else '&'
    return await rawImport("#{url}#{sep}v=#{Date.now()}")

module.exports = {ensureComponentCookie, resetComponentCookie, importComponentModule, sameOriginify, MINT_PATH}
